import { EBool } from '~/src/dash/api/types/bool';
import { generateOptionsListAsPromise } from '~/src/dash/components/dropdowns/common';
import { convertStatusFilterValueIntoString } from '~/src/dash/components/filters/conversions';
import { IExtendedIFilterRowObject } from '~/src/dash/components/filters/FilterRowV2';
import { StatusOptions } from '~/src/dash/components/filters/status';

import {
  IListViewTableColumns,
  IListViewTableRow,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  CurrencyRender,
  CurrencyRenderWithOtherCurrencies,
  EllipsisWithTooltipRender,
  PercentTrendRender,
  TruncatedListRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { IChannelRow, orderDirection } from '~/src/dash/views/channels/api';

export const formatDataToLVTRows = (
  data: IChannelRow[],
  cfg: IChannelsTable,
): IListViewTableRow[] => {
  const rows = [] as IListViewTableRow[];
  data.forEach((datum) => {
    const row = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = datum[responseKey as keyof IChannelRow];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      row[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    rows.push(row);
  });
  return rows;
};

interface IChannelsTable {
  columns: IListViewTableColumns;
}

export interface IChannelsConfig {
  cypressClassName: string;
  defaultState: {
    activeColumnKeys: string[];
    isTableExpanded: boolean;
    orderDirection: orderDirection;
    orderBy: string;
    page: number;
    rowsPerPage: RowsPerPage;
    searchInput: string;
  };
  columnSelector: {
    resetKeys?: string[];
  };
  filters: Record<string, IExtendedIFilterRowObject>;
  sorts: string[];
  table: IChannelsTable;
  trends: string[];
}

export const channelsConfig = {
  cypressClassName: 'channelsViewAll',
  defaultState: {
    activeColumnKeys: [
      'PastWeekRevenue',
      'PastWeekImpressions',
      'PastWeekEndpointRequests',
      'BidFloor',
      'ChannelLabels',
    ],
    isTableExpanded: false,
    orderDirection: 'desc',
    orderBy: 'past_week_revenue',
    page: 0,
    rowsPerPage: 25,
    searchInput: '',
  },
  columnSelector: {
    resetKeys: [
      'PastWeekRevenue',
      'PastWeekImpressions',
      'PastWeekEndpointRequests',
      'BidFloor',
      'ChannelLabels',
    ],
  },
  filters: {
    Status: {
      key: 'Status',
      tKey: 'Status',
      getList: () => generateOptionsListAsPromise(StatusOptions),
      selectedLimit: 1,
      isSelectionRequired: true,
      selectedOptionIDs: new Set([2]), // Active
      disableSearch: true,
      disableSort: true,
      displayLabelWithSelection: true,
      selectedOptionIDsOnReset: new Set([0]), // Show All
      queryKey: 'activeFilter',
      formatQueryValue: convertStatusFilterValueIntoString,
    },
  },
  sorts: ['PastWeekRevenue', 'PastWeekImpressions', 'PastWeekEndpointRequests'],
  trends: ['PastWeekRevenueTrend', 'PastWeekImpressionsTrend'],
  table: {
    columns: {
      ID: {
        formatValue: (value: number) => value.toString(),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        tKey: 'ID',
        label: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
      },
      Status: {
        defaultOrderDirection: 'desc',
        formatValue: (value: number) => value === EBool.EBool_TRUE,
        headerRenderType: 'switch',
        key: 'Status',
        tKey: 'Status',
        label: 'Status',
        requestKey: 'active',
        responseKey: 'Active',
        valueType: 'boolean',
      },
      Name: {
        cellJSX: EllipsisWithTooltipRender,
        cellRenderType: 'name_link',
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        tKey: 'Name',
        label: 'Name',
        requestKey: 'name',
        responseKey: 'Name',
        valueType: 'string',
      },
      ExchangeRate: {
        isHidden: true,
        isSearchable: true,
        key: 'ExchangeRate',
        tKey: 'ExchangeRate',
        label: 'ExchangeRate',
        requestKey: 'ExchangeRate',
        responseKey: 'ExchangeRate',
        valueType: 'object',
      },
      ChannelLabels: {
        cellJSX: TruncatedListRender,
        disableSort: true,
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ChannelLabels',
        tKey: 'ChannelLabels',
        label: 'Channel Labels',
        requestKey: 'channel_labels',
        responseKey: 'ChannelLabels',
        valueType: 'array',
      },
      BidFloor: {
        cellJSX: CurrencyRenderWithOtherCurrencies,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        isDefaultActive: true,
        isNumeric: true,
        key: 'BidFloor',
        tKey: 'FloorPrice',
        label: 'Floor Price',
        orderByKey: 'bid_floor',
        requestKey: '',
        responseKey: 'BidFloor',
        sortIcon: 'arrow_down',
        sortLabel: 'Top Revenue',
        sortLabelTKey: 'TopRevenue',
        valueType: 'number',
      },
      Last24HrAverageCPM: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Average CPM Last 24 Hours',
        headerTooltipTKey: 'TooltipAvgCPML24H',
        isDefaultActive: true,
        isNumeric: true,
        key: 'Last24HrAverageCPM',
        tKey: 'AvgCPML24H',
        label: 'Avg. CPM (L24H)',
        orderByKey: 'last_24_hr_average_cpm',
        requestKey: '',
        responseKey: 'Last24HrAverageCPM',
        sortIcon: 'arrow_down',
        sortLabel: 'Top Avg. CPM',
        sortLabelTKey: 'TopAvgCPML24H',
        valueType: 'number',
      },
      PastWeekAverageCPM: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Average CPM Last 7 Days',
        headerTooltipTKey: 'TooltipAvgCPML7D',
        isDefaultActive: true,
        isNumeric: true,
        key: 'PastWeekAverageCPM',
        tKey: 'AvgCPML7D',
        label: 'Avg. CPM (L7D)',
        orderByKey: 'past_week_average_cpm',
        requestKey: '',
        responseKey: 'PastWeekAverageCPM',
        sortIcon: 'arrow_down',
        sortLabel: 'Top Avg. CPM',
        sortLabelTKey: 'TopAvgCPML7D',
        valueType: 'number',
      },
      Last24HrRevenue: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Revenue Last 24 Hours',
        headerTooltipTKey: 'TooltipRevL24H',
        isDefaultActive: true,
        isNumeric: true,
        key: 'Last24HrRevenue',
        tKey: 'RevL24H',
        label: 'Rev. (L24H)',
        orderByKey: 'last_24_hr_revenue',
        requestKey: '',
        responseKey: 'Last24HrRevenue',
        sortIcon: 'arrow_down',
        sortLabel: 'Top Revenue',
        sortLabelTKey: 'TopRevenueL24H',
        valueType: 'number',
      },
      PastWeekRevenue: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Revenue Last 7 Days',
        headerTooltipTKey: 'TooltipRevL7D',
        isDefaultActive: true,
        isNumeric: true,
        key: 'PastWeekRevenue',
        tKey: 'RevL7D',
        label: 'Rev. (L7D)',
        orderByKey: 'past_week_revenue',
        requestKey: '',
        responseKey: 'PastWeekRevenue',
        sortIcon: 'arrow_down',
        sortLabel: 'Top Revenue (L7D)',
        sortLabelTKey: 'TopRevenueL7D',
        trendKey: 'PastWeekRevenueTrend',
        valueType: 'number',
      },
      PastWeekRevenueTrend: {
        cellJSX: PercentTrendRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Revenue Trend L7D Compared to Previous Period',
        headerTooltipTKey: 'TooltipRevenueTrend',
        isDefaultActive: true,
        key: 'PastWeekRevenueTrend',
        tKey: 'RevTrend',
        label: 'Rev. Trend',
        orderByKey: 'past_week_revenue_trend',
        parentKey: 'PastWeekRevenue',
        requestKey: '',
        responseKey: 'PastWeekRevenueTrend',
        valueType: 'number',
      },
      Last24HrImpressions: {
        cellRenderType: 'abbreviated_number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Impressions Last 24 Hours',
        headerTooltipTKey: 'TooltipImpL24H',
        isDefaultActive: true,
        isNumeric: true,
        key: 'Last24HrImpressions',
        tKey: 'ImpL24H',
        label: 'Imp. (L24H)',
        orderByKey: 'last_24_hr_impressions',
        requestKey: '',
        responseKey: 'Last24HrImpressions',
        sortIcon: 'arrow_down',
        sortLabel: 'Top Impressions',
        sortLabelTKey: 'TopImpressionsL24H',
        valueType: 'number',
      },
      PastWeekImpressions: {
        cellRenderType: 'abbreviated_number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Impressions Last 7 Days',
        headerTooltipTKey: 'TooltipImpL7D',
        isDefaultActive: true,
        isNumeric: true,
        key: 'PastWeekImpressions',
        tKey: 'ImpL7D',
        label: 'Imp. (L7D)',
        orderByKey: 'past_week_impressions',
        requestKey: '',
        responseKey: 'PastWeekImpressions',
        sortIcon: 'arrow_down',
        sortLabel: 'Top Impressions (L7D)',
        sortLabelTKey: 'TopImpressionsL7D',
        trendKey: 'PastWeekImpressionsTrend',
        valueType: 'number',
      },
      PastWeekImpressionsTrend: {
        cellJSX: PercentTrendRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Impressions Trend L7D Compared to Previous Period',
        headerTooltipTKey: 'TooltipImpressionsTrend',
        isDefaultActive: true,
        key: 'PastWeekImpressionsTrend',
        tKey: 'ImpTrend',
        label: 'Imp. Trend',
        orderByKey: 'past_week_impressions_trend',
        parentKey: 'PastWeekImpressions',
        requestKey: '',
        responseKey: 'PastWeekImpressionsTrend',
        valueType: 'number',
      },
      Last24HrEndpointRequests: {
        cellRenderType: 'abbreviated_number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Endpoint Requests Last 24 Hours',
        headerTooltipTKey: 'TooltipEndpointReqL24H',
        isDefaultActive: true,
        isNumeric: true,
        key: 'Last24HrEndpointRequests',
        tKey: 'EndpointReqL24H',
        label: 'Endpoint Req. (L24H)',
        orderByKey: 'last_24_hr_endpoint_requests',
        requestKey: '',
        responseKey: 'Last24HrEndpointRequests',
        sortIcon: 'arrow_down',
        sortLabel: 'Top Requests',
        sortLabelTKey: 'TopEndpointReqsL24H',
        valueType: 'number',
      },
      PastWeekEndpointRequests: {
        cellRenderType: 'abbreviated_number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Endpoint Requests Last 7 Days',
        headerTooltipTKey: 'TooltipEndpointReqL7D',
        isDefaultActive: true,
        isNumeric: true,
        key: 'PastWeekEndpointRequests',
        tKey: 'EndpointReqL7D',
        label: 'Endpoint Req. (L7D)',
        orderByKey: 'past_week_endpoint_requests',
        requestKey: '',
        responseKey: 'PastWeekEndpointRequests',
        sortIcon: 'arrow_down',
        sortLabel: 'Top Endpoint Requests (L7D)',
        sortLabelTKey: 'TopEndpointReqsL7D',
        valueType: 'number',
      },
      Actions: {
        cellRenderType: 'actions',
        disableSort: true,
        headerRenderType: 'actions',
        key: 'Actions',
        tKey: 'Actions',
        label: 'Actions',
        requestKey: '',
        responseKey: '',
        valueType: 'component',
      },
    },
  },
} as IChannelsConfig;
